import { CANVAS_COLORS } from '../../utils/tokens/canvas'
import { getCornerRadiusPercent, type MobileCanvasState } from './mobileBoard'

const createImageElement = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    image.src = src
  })

const percentToPixels = (value: string, width: number, height: number) => {
  const numeric = Number.parseFloat(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return 0
  return (Math.min(width, height) * numeric) / 100
}

const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  const clippedRadius = Math.max(0, Math.min(radius, width / 2, height / 2))

  ctx.beginPath()
  ctx.moveTo(x + clippedRadius, y)
  ctx.lineTo(x + width - clippedRadius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + clippedRadius)
  ctx.lineTo(x + width, y + height - clippedRadius)
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - clippedRadius,
    y + height
  )
  ctx.lineTo(x + clippedRadius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - clippedRadius)
  ctx.lineTo(x, y + clippedRadius)
  ctx.quadraticCurveTo(x, y, x + clippedRadius, y)
  ctx.closePath()
}

const drawCoverImage = (
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  sourceWidth: number,
  sourceHeight: number
) => {
  const targetAspect = width / Math.max(1, height)
  const sourceAspect = sourceWidth / Math.max(1, sourceHeight)

  let cropWidth = sourceWidth
  let cropHeight = sourceHeight
  let cropX = 0
  let cropY = 0

  if (sourceAspect > targetAspect) {
    cropWidth = sourceHeight * targetAspect
    cropX = (sourceWidth - cropWidth) / 2
  } else {
    cropHeight = sourceWidth / Math.max(0.1, targetAspect)
    cropY = (sourceHeight - cropHeight) / 2
  }

  ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, x, y, width, height)
}

export const renderMobileBoardThumbnailBlob = async (
  state: MobileCanvasState
): Promise<Blob> => {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(state.width))
  canvas.height = Math.max(1, Math.round(state.height))

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to create thumbnail canvas context')
  }

  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height)
  gradient.addColorStop(0, 'rgba(255,255,255,0.55)')
  gradient.addColorStop(1, 'rgba(253,252,249,0.92)')
  ctx.fillStyle = CANVAS_COLORS.background.board
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const radiusPercent = getCornerRadiusPercent(state.cornerStyle)
  const loadedImages = await Promise.all(
    state.elements.map(async (element) => {
      const image = await createImageElement(element.src)
      return { element, image }
    })
  )

  loadedImages.forEach(({ element, image }) => {
    const x = element.left
    const y = element.top
    const width = element.width
    const height = element.height
    const radius = percentToPixels(radiusPercent, width, height)

    ctx.save()
    roundRectPath(ctx, x, y, width, height, radius)
    ctx.clip()
    drawCoverImage(
      ctx,
      image,
      x,
      y,
      width,
      height,
      image.naturalWidth || width,
      image.naturalHeight || height
    )
    ctx.restore()

    ctx.strokeStyle = 'rgba(61,59,54,0.08)'
    ctx.lineWidth = 2
    roundRectPath(ctx, x, y, width, height, radius)
    ctx.stroke()
  })

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(
          new Error('Failed to convert mobile board thumbnail canvas to blob')
        )
        return
      }
      resolve(blob)
    }, 'image/png')
  })
}
